/**
 * Cross-sell Product Variant Handler
 * Updates the checkbox value and prices when variant options change
 * Syncs variant selection with main product based on dimension pattern
 */
class CrossSellProduct {
  // Regex to match dimension patterns like "90x190", "140x200", "160 x 200", etc.
  static DIMENSION_REGEX = /(\d+)\s*[xX×]\s*(\d+)/;
  
  constructor(container) {
    this.container = container;
    this.checkbox = container.querySelector('input.switch[name="bundles"]');
    this.selects = container.querySelectorAll('[data-cross-sell-option]');
    this.quantityInput = container.querySelector('[data-cross-sell-quantity-selector] .quantity__input');
    this.productId = this.checkbox?.getAttribute('data-cross-sell-product-id');
    
    // Price elements
    this.originalPriceEl = container.querySelector('[data-cross-sell-original-price]');
    this.finalPriceEl = container.querySelector('[data-cross-sell-final-price]');
    this.savingsEl = container.querySelector('[data-cross-sell-savings]');
    this.badgeEl = container.querySelector('.cross-sell-product__badge');
    
    // Discount settings
    this.discountType = container.getAttribute('data-discount-type');
    this.discountValue = parseFloat(container.getAttribute('data-discount-value')) || 0;
    this.discountFixed = parseFloat(container.getAttribute('data-discount-fixed')) || 0;
    
    if (!this.checkbox || !this.productId) return;
    
    this.variants = [];
    this.fetchVariants();
    this.bindEvents();
    this.subscribeToMainVariantChange();
  }
  
  async fetchVariants() {
    try {
      const response = await fetch(`/products/${this.getProductHandle()}.js`);
      const product = await response.json();
      this.variants = product.variants;
    } catch (error) {
      console.error('Error fetching product variants:', error);
    }
  }
  
  getProductHandle() {
    return this.container.getAttribute('data-product-handle');
  }
  
  bindEvents() {
    this.selects.forEach(select => {
      select.addEventListener('change', () => this.updateVariant());
    });
  }
  
  /**
   * Subscribe to main product variant changes
   * When main product variant changes, sync cross-sell variant if dimensions match
   */
  subscribeToMainVariantChange() {
    if (typeof theme === 'undefined' || !theme.pubsub) return;
    
    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
      const mainVariant = event.data?.variant;
      if (!mainVariant) return;
      
      this.syncWithMainVariant(mainVariant);
    });
  }
  
  /**
   * Extract dimension pattern from variant title
   * @param {string} variantTitle - The variant title to extract dimensions from
   * @returns {string|null} - Normalized dimension string (e.g., "140x200") or null
   */
  extractDimension(variantTitle) {
    if (!variantTitle) return null;
    
    const match = variantTitle.match(CrossSellProduct.DIMENSION_REGEX);
    if (match) {
      // Return normalized format: "140x200"
      return `${match[1]}x${match[2]}`;
    }
    return null;
  }
  
  /**
   * Sync cross-sell variant with main product variant based on dimension
   * @param {Object} mainVariant - The main product's selected variant
   */
  syncWithMainVariant(mainVariant) {
    if (this.variants.length === 0) return;
    
    // Extract dimension from main variant
    const mainDimension = this.extractDimension(mainVariant.title);
    if (!mainDimension) return;
    
    // Find matching variant in cross-sell product
    const matchingVariant = this.variants.find(variant => {
      const crossSellDimension = this.extractDimension(variant.title);
      return crossSellDimension === mainDimension;
    });
    
    if (!matchingVariant) return;
    
    // Update select elements if they exist
    if (this.selects.length > 0) {
      this.updateSelectsForVariant(matchingVariant);
    }
    
    // Update checkbox value and price
    const originalPrice = matchingVariant.price;
    const discountAmount = this.calculateDiscount(originalPrice);
    const discountedPrice = Math.max(0, originalPrice - discountAmount);
    
    this.checkbox.value = matchingVariant.id;
    this.checkbox.setAttribute('data-price', discountedPrice);
    
    // Update displays
    this.updatePriceDisplay(originalPrice, discountAmount, discountedPrice);
    this.updateDimensionsDisplay(matchingVariant);
  }
  
  /**
   * Update select elements to reflect a specific variant
   * @param {Object} variant - The variant to select
   */
  updateSelectsForVariant(variant) {
    if (!variant.options) return;
    
    variant.options.forEach((optionValue, index) => {
      const select = this.selects[index];
      if (select) {
        // Find and select the matching option
        const option = Array.from(select.options).find(opt => opt.value === optionValue);
        if (option) {
          select.value = optionValue;
        }
      }
    });
  }
  
  calculateDiscount(price) {
    if (this.discountType === 'percentage') {
      return Math.round(price * this.discountValue / 100);
    } else if (this.discountType === 'fixed') {
      return this.discountFixed;
    }
    return 0;
  }
  
  formatMoney(cents) {
    // Use theme's currency formatter
    if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    // Fallback
    const euros = cents / 100;
    return euros.toFixed(2).replace('.', ',') + ' €';
  }
  
  updatePriceDisplay(originalPrice, discountAmount, discountedPrice) {
    // Update original price
    if (this.originalPriceEl) {
      this.originalPriceEl.innerHTML = this.formatMoney(originalPrice);
      this.originalPriceEl.classList.toggle('hidden', discountAmount === 0);
    }
    
    // Update final price
    if (this.finalPriceEl) {
      this.finalPriceEl.innerHTML = '+' + this.formatMoney(discountedPrice);
      this.finalPriceEl.classList.toggle('text-accent', discountAmount > 0);
    }
    
    // Update savings badge
    if (this.savingsEl) {
      this.savingsEl.innerHTML = this.formatMoney(discountAmount);
    }
    if (this.badgeEl) {
      this.badgeEl.classList.toggle('hidden', discountAmount === 0);
    }
  }
  
  updateVariant() {
    if (this.variants.length === 0) return;
    
    const selectedOptions = Array.from(this.selects).map(select => select.value);
    
    const matchingVariant = this.variants.find(variant => {
      return variant.options.every((option, index) => option === selectedOptions[index]);
    });
    
    if (matchingVariant) {
      const originalPrice = matchingVariant.price;
      const discountAmount = this.calculateDiscount(originalPrice);
      const discountedPrice = Math.max(0, originalPrice - discountAmount);
      
      // Update checkbox
      this.checkbox.value = matchingVariant.id;
      this.checkbox.setAttribute('data-price', discountedPrice);
      
      // Update price display
      this.updatePriceDisplay(originalPrice, discountAmount, discountedPrice);
      
      // Update dimensions display
      this.updateDimensionsDisplay(matchingVariant);
    }
  }
  
  /**
   * Update the dimensions display with the selected variant title
   * @param {Object} variant - The selected variant
   */
  updateDimensionsDisplay(variant) {
    const dimensionsEl = this.container.querySelector('[data-cross-sell-dimensions] span:last-child');
    if (dimensionsEl && variant.title && variant.title !== 'Default Title') {
      dimensionsEl.textContent = `Dimensions : ${variant.title}`;
    }
  }
}

/**
 * Cross-sell Clear All Button Handler
 */
class CrossSellClearAll {
  constructor() {
    this.clearButtons = document.querySelectorAll('[data-cross-sell-clear-all]');
    this.checkboxes = document.querySelectorAll('.cross-sell-product input.switch[name="bundles"]');
    
    if (this.clearButtons.length === 0 || this.checkboxes.length === 0) return;
    
    this.bindEvents();
    this.updateVisibility();
  }
  
  bindEvents() {
    // Clear all button click
    this.clearButtons.forEach(button => {
      button.addEventListener('click', () => this.clearAll());
    });
    
    // Watch checkbox changes
    this.checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => this.updateVisibility());
    });
  }
  
  clearAll() {
    this.checkboxes.forEach(checkbox => {
      checkbox.checked = false;
      // Dispatch change event for any listeners
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    });
    this.updateVisibility();
  }
  
  updateVisibility() {
    const anyChecked = Array.from(this.checkboxes).some(cb => cb.checked);
    this.clearButtons.forEach(button => {
      button.hidden = !anyChecked;
    });
  }
}

// Initialize cross-sell products
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.cross-sell-product').forEach(container => {
    new CrossSellProduct(container);
  });
  
  // Initialize clear all button
  new CrossSellClearAll();
});

// Re-initialize on section load (for Shopify theme editor)
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('.cross-sell-product').forEach(container => {
    new CrossSellProduct(container);
  });
  
  // Re-initialize clear all button
  new CrossSellClearAll();
});
